/**
 * In-app notification display + routing helpers.
 * `notifications/{uid}/items` documents are written by Cloud Functions and legacy code
 * with different field shapes (title vs preview, read vs readAt, href vs meta only).
 */

/** Canonical unread rule (RR-57/58): read === true OR readAt is present (any Firestore shape). */
export function isNotificationRead(n: { read?: unknown; readAt?: unknown } | null | undefined): boolean {
  if (!n) return true;
  const r = n.read;
  if (r === true || r === 'true' || r === 1 || r === '1') return true;
  if (r === false || r === 'false' || r === 0) {
    /* fall through — readAt may still indicate read */
  }
  const ra = n.readAt;
  if (ra == null) return false;
  if (typeof ra === 'object') {
    const o = ra as Record<string, unknown>;
    if (typeof o.toDate === 'function') return true;
    if (typeof o.seconds === 'number') return true;
  }
  return Boolean(ra);
}

export function isNotificationUnread(n: { read?: unknown; readAt?: unknown } | null | undefined): boolean {
  return !isNotificationRead(n);
}

/** Only allow same-origin app paths (blocks //evil.com). */
export function safeInternalPath(href: unknown): string | null {
  if (typeof href !== 'string') return null;
  const t = href.trim();
  if (!t.startsWith('/') || t.startsWith('//')) return null;
  return t;
}

export function getNotificationHeadline(n: Record<string, unknown>): string {
  const title = typeof n.title === 'string' ? n.title.trim() : '';
  if (title) return title;

  const type = typeof n.type === 'string' ? n.type : '';
  if (type === 'message') {
    const from =
      typeof n.fromName === 'string' && n.fromName.trim() ? (n.fromName as string).trim() : 'Someone';
    return `Message from ${from}`;
  }
  if (type === 'application') return 'Application update';
  if (type === 'offer') return 'Offer update';
  if (type === 'new_application') return 'New application';
  if (type === 'application_status') return 'Application status';
  if (type === 'profile_viewed' || type === 'profile_view') return 'Profile view';
  if (type === 'job_posted') return 'Job posting';
  if (type === 'urgent_job') return 'Urgent deployment';
  if (type === 'job_match') return 'New job match';
  if (type === 'interview') return 'Interview update';
  if (type === 'document_expiry') return 'Document expiry reminder';
  if (type === 'workforce_invite') return 'Team invitation';
  if (type === 'workforce_invite_response') return 'Team invitation response';
  return 'Account update';
}

/** True when text looks like a Firebase UID or internal seed doc id. */
function isInternalNotificationText(value: string): boolean {
  const t = value.trim();
  if (!t) return true;
  if (/^[A-Za-z0-9]{20,32}$/.test(t)) return true;
  if (/^(seed-|qa-|test-)/i.test(t)) return true;
  if (!/\s/.test(t) && t.includes('-') && t.length >= 12) return true;
  return false;
}

function sanitizeNotificationCopy(value: string): string {
  const trimmed = value.trim();
  if (!trimmed || isInternalNotificationText(trimmed)) return '';
  return trimmed;
}

export function getNotificationBody(n: Record<string, unknown>): string {
  const preview = typeof n.preview === 'string' ? sanitizeNotificationCopy(n.preview) : '';
  const body = typeof n.body === 'string' ? sanitizeNotificationCopy(n.body) : '';
  const text = typeof n.text === 'string' ? sanitizeNotificationCopy(n.text) : '';
  if (preview) return preview;
  if (body) return body;
  if (text) return text;

  const type = typeof n.type === 'string' ? n.type : '';
  if (type === 'message') {
    return typeof n.threadId === 'string' && n.threadId
      ? 'Open Messages to read this conversation.'
      : 'Open Messages to read your inbox.';
  }
  if (type === 'application') return 'Open Applications to review this update.';
  if (type === 'offer') return 'Open Applications to review this offer.';
  if (type === 'new_application' || type === 'application_status') return 'Open Applications for details.';
  if (type === 'document_expiry') return 'Open your credentials to upload or renew documents.';
  if (type === 'workforce_invite') return 'An employer invited you to join their team. Accept or decline from your dashboard.';
  if (type === 'workforce_invite_response') return 'Open Workforce to see your team.';
  return 'Tap this notification to open the related screen.';
}
